import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { useEffect, useId, useState } from 'react';
import { useRefLookup } from '../../hooks/use-ref-lookup.js';

interface RefParamProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  refView: string;
  refLabelField: string;
  refValueField: string;
  disabled?: boolean | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
}

export function RefParam({
  id,
  value,
  onChange,
  refView,
  refLabelField,
  refValueField,
  disabled,
  error,
  required,
}: RefParamProps) {
  const listboxId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useRefLookup({
    view: refView,
    labelField: refLabelField,
    valueField: refValueField,
    q: query,
    enabled: open,
  });

  const selectedLabel = data?.items.find((i) => i.value === value)?.label ?? value;

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <div className="relative">
      {/* Static aria-expanded values so the linter sees literal booleans */}
      {/* aria-expanded: ternary always yields "true"|"false" — linter false-positive on expressions */}
      <button
        type="button"
        id={id}
        title={value === '' ? t('params.search_placeholder') : selectedLabel}
        aria-haspopup="listbox"
        // eslint-disable-next-line jsx-a11y/aria-proptypes
        aria-expanded={open ? 'true' : 'false'}
        aria-describedby={error !== undefined ? `${id}-error` : undefined}
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className={cn(
          'flex h-10 w-full items-center rounded-[var(--radius-input)] border px-3 py-2 text-left text-sm',
          'bg-[var(--color-qup-bg-card-dark)] text-[var(--color-qup-text-dark)] transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error !== undefined
            ? 'border-[var(--color-qup-error)]'
            : 'border-[var(--color-qup-border-dark)]',
        )}
      >
        <SelectedLabel value={value} label={selectedLabel} />
      </button>
      {required === true && <span className="sr-only">obligatorio</span>}

      {open && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-full rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
            'bg-[var(--color-qup-bg-card-dark)] shadow-[var(--shadow-card-hover)]',
          )}
        >
          <div className="p-2">
            <input
              type="text"
              aria-label={t('params.search_placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('params.search_placeholder')}
              className={cn(
                'w-full rounded-[var(--radius-input)] border border-[var(--color-qup-border-dark)]',
                'bg-[var(--color-qup-bg-dark)] px-3 py-1.5 text-sm text-[var(--color-qup-text-dark)]',
                'placeholder:text-[var(--color-qup-text-muted)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
              )}
            />
          </div>

          <ul
            id={listboxId}
            aria-label={t('params.search_placeholder')}
            className="max-h-48 overflow-y-auto py-1"
          >
            {isLoading && (
              <li
                aria-selected={false}
                className="px-3 py-2 text-sm text-[var(--color-qup-text-muted-dark)]"
              >
                {t('loading')}
              </li>
            )}
            {!isLoading && (data?.items.length ?? 0) === 0 && (
              <li
                aria-selected={false}
                className="px-3 py-2 text-sm text-[var(--color-qup-text-muted-dark)]"
              >
                {t('params.no_options')}
              </li>
            )}
            {data?.items.map((item) => {
              const isSelected = item.value === value;
              return (
                <li
                  key={item.value}
                  data-selected={isSelected}
                  className={
                    isSelected
                      ? 'cursor-pointer bg-[var(--color-qup-primary)]/15 px-3 py-2 text-sm text-[var(--color-qup-primary)] transition-colors duration-150'
                      : 'cursor-pointer px-3 py-2 text-sm text-[var(--color-qup-text-dark)] transition-colors duration-150 hover:bg-[var(--color-qup-bg-dark)]'
                  }
                  onClick={() => {
                    onChange(item.value);
                    setOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onChange(item.value);
                      setOpen(false);
                    }
                  }}
                >
                  {item.label}
                </li>
              );
            })}
          </ul>

          {value !== '' && (
            <div className="border-t border-[var(--color-qup-border-dark)] px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="text-xs text-[var(--color-qup-text-muted-dark)] hover:text-[var(--color-qup-primary)]"
              >
                Limpiar
              </button>
            </div>
          )}
        </div>
      )}

      {error !== undefined && (
        <span
          id={`${id}-error`}
          role="alert"
          className="mt-1 block text-xs text-[var(--color-qup-error)]"
        >
          {error}
        </span>
      )}
    </div>
  );
}

function SelectedLabel({ value, label }: { value: string; label: string }) {
  return (
    <>
      <span
        className={cn('flex-1 truncate', value === '' ? 'text-[var(--color-qup-text-muted)]' : '')}
      >
        {value === '' ? '—' : label}
      </span>
      <svg
        className="h-4 w-4 shrink-0 text-[var(--color-qup-text-muted-dark)]"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </>
  );
}
